import { StrategyBuilderFunction, EMPTY_STRATEGY } from './types';
import { addLongCall, addShortCall } from './addOption';
import {
  getDefaultExpiration,
  findClosestStrikePrice,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from './utils';

export const shortCallCondor: StrategyBuilderFunction = ({
  chain,
  spot,
  expirationDate,
  expirationKey,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  const strikesAtExpiration = expirationKey
    ? getStrikesAtExpiration(chain, expirationKey)
    : getDefaultExpiration(chain);

  if (
    !strikesAtExpiration ||
    !strikesAtExpiration.e ||
    !strikesAtExpiration.ey ||
    !strikesAtExpiration.em ||
    !strikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const expirationDateForOptions =
    expirationDate ||
    expirationToDate(strikesAtExpiration.ey, strikesAtExpiration.em, strikesAtExpiration.ed);
  const expirationKeyForOptions = expirationKey || strikesAtExpiration.e;

  // Define strike prices for the Short Call Condor
  const lowestStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -2 }),
  ); // Sell ITM call
  const lowerMiddleStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -1 }),
  ); // Buy lower high-strike ITM/ATM call
  const upperMiddleStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 1 }),
  ); // Buy higher low-strike OTM call
  const highestStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 2 }),
  ); // Sell further OTM call

  return {
    optionPositions: [
      // Sell ITM call option at the lowest strike
      addShortCall({
        strike: lowestStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[lowestStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[lowestStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Buy ITM/ATM call option at the lower middle strike
      addLongCall({
        strike: lowerMiddleStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[lowerMiddleStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[lowerMiddleStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Buy OTM call option at the upper middle strike
      addLongCall({
        strike: upperMiddleStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[upperMiddleStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[upperMiddleStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Sell further OTM call option at the highest strike
      addShortCall({
        strike: highestStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[highestStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[highestStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
    ],
    stockPositions: [],
  };
};
